import type { Graphics } from 'pixi.js';
import { lerpColor } from '../../color';
import type { WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const ROLLOVERS = 10;
const ARROWS = 4;
const STAR_LIGHTS = 12;
const COIL_TURNS = 16;

function star(g: Graphics, x: number, y: number, r: number, inner: number, rot: number, sx = 1, sy = 1): void {
  const points: number[] = [];
  for (let i = 0; i < 10; i++) {
    const a = rot - Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 === 0 ? r : r * inner;
    points.push(x + Math.cos(a) * rr * sx, y + Math.sin(a) * rr * sy);
  }
  g.poly(points, true);
}

function dash(g: Graphics, x: number, y: number, length: number, width: number, angle: number): void {
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  g.poly(
    [x + ux * length, y + uy * length, x - uy * width, y + ux * width, x - ux * length * 0.5, y - uy * length * 0.5, x + uy * width, y - ux * width],
    true,
  );
}

function ease(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return c * c * (3 - 2 * c);
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    const sx = body.scaleX;
    const sy = body.scaleY;
    g.ellipse(body.x, body.y, r * 0.6 * sx, r * 0.6 * sy).fill({ color: lerpColor(color, pal.rim, 0.3), alpha: 0.45 * alpha });
    g.ellipse(body.x, body.y, r * 0.6 * sx, r * 0.6 * sy);
    g.stroke({ width: Math.max(1, r * 0.04), color: lerpColor(color, pal.ink, 0.35), alpha: 0.3 * alpha });
    star(g, body.x, body.y, r * 0.36, 0.45, 0, sx, sy);
    g.fill({ color: lerpColor(color, pal.ink, 0.25), alpha: 0.4 * alpha });
    k.highlight(body, 0.3 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    const sx = body.scaleX;
    const sy = body.scaleY;
    k.trace(body);
    g.fill({ color: fill, alpha: 0.8 * alpha });
    const spacing = Math.max(3, r * 0.2);
    k.hatch(body, Math.PI / 4, spacing, 0.35, lerpColor(fill, pal.ink, 0.4), 0.2 * alpha, spacing * 0.3);
    g.ellipse(body.x - 0.36 * r * sx, body.y - 0.38 * r * sy, 0.2 * r * sx, 0.16 * r * sy).fill({ color: pal.heldLight, alpha: 0.7 * alpha });
    g.circle(body.x - 0.12 * r * sx, body.y - 0.56 * r * sy, 0.06 * r).fill({ color: pal.heldLight, alpha: 0.6 * alpha });
    k.trace(body);
    g.stroke({ width, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.08 * alpha });
    const lamp = Math.max(2.2, k.unit * 0.014);
    k.ringDots(body, reach, ROLLOVERS, spin, (x, y, i) => {
      const tone = i % 2 === 0 ? pal.spot : lerpColor(pal.spot, pal.rim, 0.5);
      g.circle(x, y, lamp * 2).fill({ color: tone, alpha: 0.14 * alpha });
      g.circle(x, y, lamp).fill({ color: tone, alpha: 0.75 * alpha });
      g.stroke({ width: Math.max(1, lamp * 0.35), color: pal.ink, alpha: 0.5 * alpha });
    });
    const tip = reach + k.unit * 0.022;
    const tail = reach + k.unit * 0.05;
    const wing = k.unit * 0.018;
    for (let i = 0; i < ARROWS; i++) {
      const a = spin + Math.PI / ROLLOVERS + (i * TAU) / ARROWS;
      const ux = Math.cos(a);
      const uy = Math.sin(a);
      const tx = body.x + ux * tip;
      const ty = body.y + uy * tip;
      const bx = body.x + ux * tail;
      const by = body.y + uy * tail;
      g.moveTo(bx - uy * wing, by + ux * wing).lineTo(tx, ty).lineTo(bx + uy * wing, by - ux * wing);
    }
    g.stroke({ width: edge, color: pal.spot, alpha: 0.85 * alpha, cap: 'round', join: 'round' });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.8 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.spot : pal.incoming.circle;
    if (on) g.circle(x, y, radius * 2).fill({ color: tone, alpha: 0.18 * alpha });
    g.circle(x, y, radius).fill({ color: on ? lerpColor(tone, pal.rim, 0.2) : lerpColor(pal.bg, pal.rim, 0.16), alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
    if (on) g.circle(x - radius * 0.32, y - radius * 0.34, radius * 0.24).fill({ color: pal.rim, alpha: 0.7 * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const gap = Math.max(7, k.unit * 0.034);
    const dot = Math.max(1.3, k.unit * 0.0055);
    let carry = gap * 0.5;
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const x1 = points[i]!;
      const y1 = points[i + 1]!;
      const len = Math.hypot(x1 - x0, y1 - y0);
      let at = carry;
      while (at < len) {
        const t = at / len;
        g.circle(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, dot);
        at += gap;
      }
      carry = at - len;
    }
    g.fill({ color: pal.path, alpha: Math.min(1, alpha * 3) });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) {
      star(g, x, y, size * 1.25, 0.45, rot);
      g.fill({ color, alpha });
    } else if (i % 3 === 1) {
      g.circle(x, y, size * 0.85).stroke({ width: Math.max(1, size * 0.35), color, alpha });
    } else {
      dash(g, x, y, size * 1.3, size * 0.32, rot);
      g.fill({ color, alpha });
    }
  },

  milestone(g, m, pal) {
    const pop = m.r * 0.8;
    g.circle(m.x, m.y, pop).stroke({ width: m.width, color: pal.spot, alpha: m.fade * 0.7 });
    const steps = COIL_TURNS * 6;
    const amp = m.unit * 0.012;
    for (let s = 0; s <= steps; s++) {
      const a = (s / steps) * TAU;
      const rr = m.r + amp * Math.sin(a * COIL_TURNS);
      const x = m.x + Math.cos(a) * rr;
      const y = m.y + Math.sin(a) * rr;
      if (s === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke({ width: m.width * 0.6, color: pal.incoming.circle, alpha: m.fade * 0.6, join: 'round' });
    const ring = m.r * 1.2;
    const size = m.unit * 0.018;
    for (let i = 0; i < STAR_LIGHTS; i++) {
      const on = ease((m.grow - (i / STAR_LIGHTS) * 0.55) / 0.25);
      if (on <= 0) continue;
      const a = (i / STAR_LIGHTS) * TAU - Math.PI / 2;
      star(g, m.x + Math.cos(a) * ring, m.y + Math.sin(a) * ring, size, 0.45, a);
      g.fill({ color: i % 2 === 0 ? pal.spot : pal.heldLight, alpha: m.fade * on * 0.9 });
    }
  },

  trail(g, x, y, size, angle) {
    dash(g, x, y, size * 1.6, size * 0.4, angle);
  },
};
